import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  Tabs,
  LoadingOverlay,
  Stack,
  Grid,
  ThemeIcon,
  Avatar,
  Table,
  ScrollArea,
  Checkbox,
  Divider,
  ActionIcon,
  TextInput,
  Textarea,
  Switch,
  Center,
  Card,
  Alert,
  SimpleGrid,
  CopyButton,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconBuildingStore,
  IconUser,
  IconPackage,
  IconSearch,
  IconLink,
  IconCheck,
  IconX,
  IconDeviceFloppy,
  IconEye,
  IconAlertTriangle,
  IconArrowRight,
  IconBrandInstagram,
  IconBrandFacebook,
  IconBrandTwitter,
  IconWorld,
  IconChartBar,
  IconCopy,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { ImageUploadComponent } from "../projctManagement/adminProjectManagement";
import ShopAnalyticsPanel from "../../components/ShopAnalyticsPanel";
import { API_BASE_URL } from "../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

const BASE_URL = `${API_BASE_URL}/shopfront`;

const SocialLinkRow = ({ icon: Icon, label, url, color }) => {
  if (!url) return null;
  return (
    <Group
      justify="space-between"
      p="sm"
      bg="gray.0"
      style={{ borderRadius: 8 }}
    >
      <Group gap="sm">
        <ThemeIcon variant="light" color={color} size="md" radius="sm">
          <Icon size={16} />
        </ThemeIcon>
        <div style={{ maxWidth: 200 }}>
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            {label}
          </Text>
          <Text size="sm" lineClamp={1} component="a">
            {url}
          </Text>
        </div>
      </Group>

      <CopyButton value={url} timeout={2000}>
        {({ copied, copy }) => (
          <Tooltip
            label={copied ? "Copied" : "Copy URL"}
            withArrow
            position="left"
          >
            <ActionIcon
              color={copied ? "teal" : "gray"}
              variant="subtle"
              onClick={copy}
            >
              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
};

// =============================================================================
// TAB: ANALYTICS (Admin view of a single shop's real performance)
// =============================================================================
const AnalyticsTab = ({ shopId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${BASE_URL}/shops/${shopId}/analytics`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setStats(data.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [shopId]);

  return (
    <ShopAnalyticsPanel
      stats={stats}
      loading={loading}
      subtitle="Real-time metrics for this storefront, as seen by admins."
    />
  );
};

// =============================================================================
// TAB 1: OVERVIEW (NEW)
// =============================================================================
const OverviewTab = ({ shop, navigateToProducts }) => {
  const links = shop.socialLinks || {};
  const hasLinks =
    links.instagram || links.facebook || links.twitter || links.website;

  return (
    <Stack gap="lg">
      {/* Banner & Header */}
      <Card padding="0" radius="md" withBorder>
        <Box h={200} bg="gray.2" pos="relative">
          {shop.bannerImageUrl ? (
            <div
              style={{
                backgroundImage: `url(${shop.bannerImageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                height: "100%",
              }}
            />
          ) : (
            <Center h="100%" bg="gray.1">
              <IconBuildingStore size={64} color="gray" opacity={0.3} />
            </Center>
          )}
        </Box>
        <Box px="xl" pb="xl" mt={-50} pos="relative">
          <Group align="end" wrap="nowrap">
            <Avatar
              src={shop.logoUrl}
              size={120}
              radius="md"
              className="border-[6px] border-white shadow-md bg-white"
              color="blue"
            >
              {shop.name?.charAt(0)}
            </Avatar>
            <Box pb="sm" style={{ flex: 1 }}>
              <Group justify="space-between" align="end">
                <div>
                  <Text fw={900} size="2rem" lh={1} mb={4}>
                    {shop.name}
                  </Text>
                  <Badge
                    size="lg"
                    variant="light"
                    color={shop.isActive ? "green" : "red"}
                  >
                    {shop.isActive ? "Active Store" : "Suspended"}
                  </Badge>
                </div>
              </Group>
            </Box>
          </Group>
        </Box>
      </Card>

      <Grid>
        {/* LEFT COL: Info & Socials */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack>
            <Paper p="lg" radius="md" withBorder>
              <Text fw={700} size="lg" mb="md">
                About this Shop
              </Text>
              <Text
                c={shop.description ? "dark" : "dimmed"}
                style={{ whiteSpace: "pre-wrap" }}
              >
                {shop.description || "No description provided."}
              </Text>
            </Paper>

            {/* NEW SOCIAL PRESENCE CARD */}
            <Paper p="lg" radius="md" withBorder>
              <Text fw={700} size="lg" mb="md">
                Social Presence
              </Text>
              {hasLinks ? (
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <SocialLinkRow
                    icon={IconBrandInstagram}
                    label="Instagram"
                    url={links.instagram}
                    color="pink"
                  />
                  <SocialLinkRow
                    icon={IconBrandFacebook}
                    label="Facebook"
                    url={links.facebook}
                    color="blue"
                  />
                  <SocialLinkRow
                    icon={IconBrandTwitter}
                    label="Twitter / X"
                    url={links.twitter}
                    color="cyan"
                  />
                  <SocialLinkRow
                    icon={IconWorld}
                    label="Website"
                    url={links.website}
                    color="gray"
                  />
                </SimpleGrid>
              ) : (
                <Text c="dimmed" size="sm" fs="italic">
                  No social links added yet.
                </Text>
              )}
            </Paper>

            <Paper p="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Text fw={700} size="lg">
                  Shop Owner
                </Text>
                <Badge variant="dot" color={shop.owner ? "blue" : "red"}>
                  {shop.owner ? "Assigned" : "Unassigned"}
                </Badge>
              </Group>
              {shop.owner ? (
                <Group>
                  <ThemeIcon size={56} radius="xl" color="blue" variant="light">
                    <IconUser size={28} />
                  </ThemeIcon>
                  <div>
                    <Text fw={700} size="lg">
                      {shop.owner.nickname || "Unknown Name"}
                    </Text>
                    <Text c="dimmed">{shop.owner.email}</Text>
                  </div>
                </Group>
              ) : (
                <Alert
                  color="red"
                  title="Action Required"
                  icon={<IconAlertTriangle />}
                >
                  No owner assigned. Go to "Owner Assignment" tab.
                </Alert>
              )}
            </Paper>
          </Stack>
        </Grid.Col>

        {/* RIGHT COL: Stats */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper p="lg" radius="md" withBorder h="100%">
            <Text fw={700} size="lg" mb="lg">
              At a Glance
            </Text>
            <Stack gap="xl">
              <Group>
                <ThemeIcon size="xl" variant="light" color="indigo" radius="md">
                  <IconPackage size={24} />
                </ThemeIcon>
                <div>
                  <Text c="dimmed" size="xs" fw={700} tt="uppercase">
                    Inventory
                  </Text>
                  <Text fw={800} size="2rem" lh={1}>
                    {shop._count?.products || 0}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Products Linked
                  </Text>
                </div>
              </Group>
              <Divider />
              <Button
                variant="light"
                fullWidth
                size="md"
                onClick={navigateToProducts}
                rightSection={<IconArrowRight size={18} />}
              >
                Manage Inventory
              </Button>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
// Helper component for Images (Optional wrapper)
const ImageWithFallback = ({ src, ...props }) => (
  <div
    style={{
      backgroundImage: `url(${src})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      width: props.w || "100%",
      height: props.h || "100%",
    }}
    {...props}
  />
);

// =============================================================================
// TAB 2: OWNER ASSIGNMENT
// =============================================================================
const OwnerTab = ({ shop, onRefresh }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/users/potential-owners`, {
          headers: getAuthHeaders(),
        });
        const data = await res.json();
        if (data.success) setUsers(data.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleAssign = async (userId) => {
    if (
      !window.confirm(
        "This will replace the current shop owner if one exists. Continue?",
      )
    )
      return;
    try {
      const res = await fetch(`${BASE_URL}/shops/${shop.id}/assign-owner`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Assigned", `Owner updated to ${data.data.owner.email}`);
        onRefresh();
      } else showError("Error", data.error);
    } catch (e) {
      showError("Error", "Assignment failed");
    }
  };

  const filteredUsers = users.filter((u) => {
    const term = search.toLowerCase();
    const email = u.email ? u.email.toLowerCase() : "";
    const nickname = u.nickname ? u.nickname.toLowerCase() : "";
    return email.includes(term) || nickname.includes(term);
  });

  return (
    <Stack>
      <Paper p="md" withBorder bg="blue.0" radius="md">
        <Group>
          <ThemeIcon size={40} radius="xl">
            <IconUser />
          </ThemeIcon>
          <Box style={{ flex: 1 }}>
            <Text size="xs" fw={700} tt="uppercase" c="blue.8">
              Current Owner
            </Text>
            {shop.owner ? (
              <Text fw={600}>
                {shop.owner.email}{" "}
                <span className="text-gray-500 font-normal">
                  ({shop.owner.nickname})
                </span>
              </Text>
            ) : (
              <Text fw={600} c="red">
                No Owner Assigned
              </Text>
            )}
          </Box>
        </Group>
      </Paper>

      <Divider label="Assign New Owner" labelPosition="left" />

      <TextInput
        placeholder="Search users by email..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <Card withBorder padding="0" radius="md">
        <ScrollArea h={400}>
          <Table stickyHeader highlightOnHover>
            <Table.Thead bg="gray.1">
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Current Role</Table.Th>
                <Table.Th align="right">Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredUsers.map((user) => (
                <Table.Tr
                  key={user.cuid}
                  bg={shop.userId === user.cuid ? "green.0" : undefined}
                >
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {user.email}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {user.nickname}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="outline">
                      {user.role}
                    </Badge>
                  </Table.Td>
                  <Table.Td align="right">
                    {shop.userId === user.cuid ? (
                      <Badge
                        color="green"
                        leftSection={<IconCheck size={12} />}
                      >
                        Assigned
                      </Badge>
                    ) : (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => handleAssign(user.cuid)}
                      >
                        Assign
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>
    </Stack>
  );
};

// =============================================================================
// TAB 3: PRODUCT LINKING
// =============================================================================
const ProductsTab = ({ shop, onRefresh }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [search, setSearch] = useState("");
  const { showSuccess, showError } = useNotification();

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/products/available`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();

      if (data.success) {
        setProducts(data.data);
      }
    } catch (e) {
      console.error(e);
      showError("Error", "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [shop.id]);

  // --- HANDLER: LINK PRODUCT ---
  const handleLink = async (productId) => {
    setActionLoading(productId);
    try {
      const res = await fetch(`${BASE_URL}/shops/${shop.id}/products`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ productIds: [productId] }),
      });
      const data = await res.json();

      if (data.success) {
        showSuccess("Success", "Product linked to shop");
        await fetchProducts();
        if (onRefresh) onRefresh();
      } else {
        showError("Error", data.error);
      }
    } catch (e) {
      showError("Error", "Linking failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnlink = async (productId) => {
    setActionLoading(productId);
    try {
      const res = await fetch(
        `${BASE_URL}/shops/${shop.id}/products/${productId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );

      const data = res.ok ? await res.json() : null;

      if (res.ok) {
        showSuccess("Removed", "Product unlinked successfully");
        await fetchProducts();
        if (onRefresh) onRefresh();
      } else {
        showError("Error", data?.error || "Failed to unlink");
      }
    } catch (e) {
      console.error(e);
      showError("Error", "Network error during unlink");
    } finally {
      setActionLoading(null);
    }
  };

  const filteredProducts = products.filter(
    (p) => p.title && p.title.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Stack>
      <Group justify="space-between">
        <TextInput
          placeholder="Search inventory..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          w={300}
        />
        <Button
          variant="light"
          leftSection={<IconPackage size={16} />}
          onClick={() => navigate("/admin/dashboard/product-management/create")}
        >
          Create New Product
        </Button>
      </Group>

      <Card withBorder padding="0" radius="md">
        <ScrollArea h={500}>
          <Table stickyHeader highlightOnHover verticalSpacing="sm">
            <Table.Thead bg="gray.1">
              <Table.Tr>
                <Table.Th>Product Details</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Ownership</Table.Th>
                <Table.Th align="right">Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredProducts.map((p) => {
                const isLinkedHere = p.shopFrontId === shop.id;
                const isLinkedElsewhere = p.shopFrontId && !isLinkedHere;
                const isProcessing = actionLoading === p.id;

                return (
                  <Table.Tr key={p.id} bg={isLinkedHere ? "blue.0" : undefined}>
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Avatar
                          src={p.media[0]?.url || null}
                          radius="sm"
                          size="md"
                          color="blue"
                          className="shadow-sm border border-gray-200"
                        >
                          <IconPackage size={20} />
                        </Avatar>
                        <Box>
                          <Text
                            size="sm"
                            fw={600}
                            c="blue"
                            className="hover:underline cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/admin/dashboard/product-management/manage/${p.id}`,
                              );
                            }}
                          >
                            {p.title}
                          </Text>
                          <Text size="10px" c="dimmed" tt="uppercase" fw={700}>
                            {p.productType?.replace("_", " ")}
                          </Text>
                        </Box>
                      </Group>
                    </Table.Td>

                    <Table.Td>
                      <Badge
                        size="xs"
                        variant="dot"
                        color={p.status === "AVAILABLE" ? "green" : "gray"}
                      >
                        {p.status}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      {isLinkedHere && (
                        <Badge color="blue" variant="filled">
                          Active in Shop
                        </Badge>
                      )}
                      {isLinkedElsewhere && (
                        <Tooltip label={`Linked to ID: ${p.shopFrontId}`}>
                          <Badge color="orange" variant="light">
                            Other Shop
                          </Badge>
                        </Tooltip>
                      )}
                      {!p.shopFrontId && (
                        <Badge color="gray" variant="outline">
                          Unassigned
                        </Badge>
                      )}
                    </Table.Td>

                    <Table.Td align="right">
                      {isLinkedHere ? (
                        <Button
                          color="red"
                          variant="subtle"
                          size="xs"
                          loading={isProcessing}
                          leftSection={<IconX size={14} />}
                          onClick={() => handleUnlink(p.id)}
                        >
                          Unlink
                        </Button>
                      ) : isLinkedElsewhere ? (
                        <Button
                          disabled
                          variant="subtle"
                          size="xs"
                          color="gray"
                        >
                          Unavailable
                        </Button>
                      ) : (
                        <Button
                          color="blue"
                          variant="light"
                          size="xs"
                          loading={isProcessing}
                          leftSection={<IconLink size={14} />}
                          onClick={() => handleLink(p.id)}
                        >
                          Link Product
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>
    </Stack>
  );
};
// =============================================================================
// TAB 4: SETTINGS (EDIT DETAILS)
// =============================================================================
const SettingsTab = ({ shop, onRefresh }) => {
  const [formData, setFormData] = useState({
    name: shop.name || "",
    description: shop.description || "",
    logoUrl: shop.logoUrl || "",
    bannerImageUrl: shop.bannerImageUrl || "",
    isActive: shop.isActive ?? true,
    socialLinks: {
      instagram: shop.socialLinks?.instagram || "",
      facebook: shop.socialLinks?.facebook || "",
      twitter: shop.socialLinks?.twitter || "",
      website: shop.socialLinks?.website || "",
    },
  });
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useNotification();

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/shops/${shop.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Saved", "Shop settings updated");
        onRefresh();
      } else showError("Error", data.error);
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack maxW={800} mx="auto" gap="lg">
      <Paper p="lg" radius="md" withBorder>
        <Text size="lg" fw={700} mb="md">
          General Information
        </Text>
        <Stack>
          <TextInput
            label="Shop Name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <Textarea
            label="Description"
            minRows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </Stack>
      </Paper>

      <Paper p="lg" radius="md" withBorder>
        <Text size="lg" fw={700} mb="md">
          Social Media
        </Text>
        <SimpleGrid cols={2}>
          <TextInput
            label="Instagram"
            placeholder="URL"
            leftSection={<IconBrandInstagram size={16} />}
            value={formData.socialLinks.instagram}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  instagram: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Facebook"
            placeholder="URL"
            leftSection={<IconBrandFacebook size={16} />}
            value={formData.socialLinks.facebook}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  facebook: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Twitter / X"
            placeholder="URL"
            leftSection={<IconBrandTwitter size={16} />}
            value={formData.socialLinks.twitter}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  twitter: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Website"
            placeholder="URL"
            leftSection={<IconWorld size={16} />}
            value={formData.socialLinks.website}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  website: e.target.value,
                },
              })
            }
          />
        </SimpleGrid>
      </Paper>

      <Paper p="lg" radius="md" withBorder>
        <Text size="lg" fw={700} mb="md">
          Visuals
        </Text>
        <Grid>
          <Grid.Col span={4}>
            <Text size="sm" fw={500} mb={4}>
              Logo
            </Text>
            <Box h={150}>
              <ImageUploadComponent
                existingUrl={formData.logoUrl}
                onUpload={(url) => setFormData({ ...formData, logoUrl: url })}
              />
            </Box>
          </Grid.Col>
          <Grid.Col span={8}>
            <Text size="sm" fw={500} mb={4}>
              Banner
            </Text>
            <Box h={150}>
              <ImageUploadComponent
                existingUrl={formData.bannerImageUrl}
                onUpload={(url) =>
                  setFormData({ ...formData, bannerImageUrl: url })
                }
              />
            </Box>
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper
        p="lg"
        radius="md"
        withBorder
        bg={formData.isActive ? undefined : "red.0"}
      >
        <Group justify="space-between">
          <Box>
            <Text fw={700}>Shop Status</Text>
            <Text size="sm" c="dimmed">
              {formData.isActive ? "Visible to users." : "Hidden from app."}
            </Text>
          </Box>
          <Switch
            size="lg"
            checked={formData.isActive}
            onChange={(e) =>
              setFormData({ ...formData, isActive: e.currentTarget.checked })
            }
            color="green"
            onLabel="ON"
            offLabel="OFF"
            thumbIcon={
              formData.isActive ? <IconCheck size={12} /> : <IconX size={12} />
            }
          />
        </Group>
      </Paper>

      <Group justify="flex-end">
        <Button
          size="md"
          leftSection={<IconDeviceFloppy size={18} />}
          onClick={handleSave}
          loading={loading}
        >
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
};

// =============================================================================
// MAIN WORKSPACE
// =============================================================================
export default function ShopfrontWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview"); // Default to Overview
  const { showError } = useNotification();

  const fetchShop = async () => {
    try {
      const res = await fetch(`${BASE_URL}/shops`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        const found = data.data.find((s) => s.id === id);
        if (found) setShop(found);
        else navigate("/admin/dashboard/product-management/shopfronts");
      }
    } catch (e) {
      showError("Error", "Failed to load shop");
      navigate("/admin/dashboard/product-management/shopfronts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShop();
  }, [id]);

  if (loading) return <LoadingOverlay visible />;
  if (!shop) return <Text>Shop not found</Text>;

  return (
    <Box>
      {/* HEADER */}
      <Group mb="lg">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={18} />}
          onClick={() =>
            navigate("/admin/dashboard/product-management/shopfronts")
          }
        >
          Back to List
        </Button>
        <Divider orientation="vertical" />
        <Group>
          <Avatar src={shop.logoUrl} radius="sm" color="blue">
            {shop.name?.charAt(0)}
          </Avatar>
          <Stack gap={0}>
            <Text fw={700} size="lg">
              {shop.name}
            </Text>
            <Badge size="xs" color={shop.isActive ? "green" : "red"}>
              {shop.isActive ? "Active" : "Suspended"}
            </Badge>
          </Stack>
        </Group>
      </Group>

      {/* TABS */}
      <Tabs value={activeTab} onChange={setActiveTab} radius="md">
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="analytics" leftSection={<IconEye size={16} />}>
            Analytics
          </Tabs.Tab>
          <Tabs.Tab value="owner" leftSection={<IconUser size={16} />}>
            Owner Assignment
          </Tabs.Tab>
          <Tabs.Tab value="products" leftSection={<IconPackage size={16} />}>
            Product Linking
          </Tabs.Tab>
          <Tabs.Tab
            value="settings"
            leftSection={<IconBuildingStore size={16} />}
          >
            Settings
          </Tabs.Tab>
        </Tabs.List>

        <Paper p="xl" radius="md" withBorder mih={400} pos="relative">
          <Tabs.Panel value="overview">
            <OverviewTab
              shop={shop}
              navigateToProducts={() => setActiveTab("products")}
            />
          </Tabs.Panel>

          <Tabs.Panel value="analytics">
            <AnalyticsTab shopId={shop.id} />
          </Tabs.Panel>

          <Tabs.Panel value="owner">
            <OwnerTab shop={shop} onRefresh={fetchShop} />
          </Tabs.Panel>

          <Tabs.Panel value="products">
            <ProductsTab shop={shop} onRefresh={fetchShop} />
          </Tabs.Panel>

          <Tabs.Panel value="settings">
            <SettingsTab shop={shop} onRefresh={fetchShop} />
          </Tabs.Panel>
        </Paper>
      </Tabs>
    </Box>
  );
}
